<template>
  <div class="tree-item__container">
    <div
      v-if="modelTreeItemRef.children"
      class="tree-item__complex"
    >
      <div class="tree-item__complex__title">
        <span
          class="material-icons tree-item__complex__title__icon-collapsed"
          @click="toggleCollapsed"
        >
          {{ modelTreeItemRef.collapsed ? 'chevron_right' : 'expand_more' }}
        </span>
        <Checkbox
          v-model="modelTreeItemRef.active"
          class="tree-item__checkbox"
          :binary="true"
          @click="changeStatusItem"
        />
        <label
          class="tree-item__title"
          :title="modelTreeItemRef.label"
          @click="changeStatusItem"
        >
          {{ modelTreeItemRef.label }}
        </label>
      </div>
      <div
        v-if="!modelTreeItemRef.collapsed"
        class="tree-item__complex__group-children"
      >
        <TreeItem
          v-for="(child) in modelTreeItemRef.children"
          :key="getDescendantProp(child, props___.identificatorKey)"
          v-bind="{
            label: getDescendantProp(child, props___.labelKey),
            active: getDescendantProp(child, props___.activeKey),
            children: getDescendantProp(child, props___.childrenKey),
            identificator: getDescendantProp(child, props___.identificatorKey),
            labelKey: props___.labelKey,
            activeKey: props___.activeKey,
            childrenKey: props___.childrenKey,
            identificatorKey: props___.identificatorKey,
            legend: child.legend
          }"
          @change-active="(item) => emits('changeActive', item)"
          @set-transparency="passTransparency"
        />
      </div>
    </div>
    <div
      v-else
      class="tree-item__simple"
    >
      <Panel
        toggleable
        collapsed
      >
        <template #header>
          <div>
            <Checkbox
              v-model="modelTreeItemRef.active"
              class="tree-item__checkbox"
              :binary="true"
              @click="changeStatusItem"
            />
            <div class="tree-item__legend__content__img">
              <img
                :src="props___.legend"
              />
            </div>
            <label
              class="tree-item__title"
              :title="modelTreeItemRef.label"
              @click="changeStatusItem"
            >
              {{ modelTreeItemRef.label }}
            </label>
          </div>
        </template>
        <template #default>
          <Slider
            v-model="transparency100"
            :min="0"
            :max="100"
            :step="1"
            @update:model-value="setTransparency"
          />
        </template>
      </Panel>
    </div>
    <div>
    </div>
  </div>
</template>


<script setup>
import {ref, computed, watch} from 'vue';
import Checkbox from 'primevue/checkbox';
import Slider from 'primevue/slider';
import ModelTreeItem from './tree-item.component-model';
import Panel from 'primevue/panel';
import TreeItem from './TreeItem';

import {
  useImmer,
} from '@sttedra_ui/framework';

// #region Props, Emits
const props___ = defineProps({
  label: {
    type: String,
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  children: {
    type: Array,
    default: null,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  labelKey: {
    type: String,
    default: 'label',
  },
  activeKey: {
    type: String,
    default: 'active',
  },
  childrenKey: {
    type: String,
    default: 'children',
  },
  identificator: {
    type: [Number, String],
    default: null,
  },
  identificatorKey: {
    type: String,
    default: 'id',
  },
  legend: {
    type: String,
    default: null,
  },
});
const emits = defineEmits(['changeActive', 'setTransparency']);
// #endregion


// #region Params, Ref, Reactives, Computed
const modelTreeItem = new ModelTreeItem(props___);
const {
  ref: modelTreeItemRef,
  update: updateModelTreeItemRef,
} = useImmer(modelTreeItem);
const transparency100 = ref(0);
const transparency = computed(() => {
  if (!!transparency100?.value) {
    return transparency100.value / 100;
  }
  return 0;
});
// #endregion


// #region Functions
async function setTransparency() {
  const params = {
    id: props___.identificator,
    transparency: transparency.value,
  };
  emits('setTransparency', params);
}
async function passTransparency(params) {
  emits('setTransparency', params);
}
function updateModelTreeItem(values) {
  updateModelTreeItemRef((draft) => {
    draft.update(values);
    return draft;
  });
}

function changeStatusItem() {
  emits('changeActive', modelTreeItemRef.value);
}

function toggleCollapsed() {
  updateModelTreeItem({
    collapsed: !modelTreeItemRef.value.collapsed,
  });
}


/**
 * Find the property of the object and it is returned
 *
 * @param {Object} obj
 * @param {String} desc
 *
 * @return {Any}
 */
function getDescendantProp(obj, desc) {
  const arr = desc.split('.');
  while (arr.length && (obj = obj[arr.shift()]));
  return obj;
}

// #endregion


// #region Effects, Hooks
watch(props___, () => {
  updateModelTreeItem(props___);
});
// #endregion


// #region Provide, Expose
// ...
// #endregion
</script>


<style lang="scss" scoped>
.tree-item {

  &__container {
    background-color: rgba($brand__color--blue, .10);
    margin-top: 5px;
    border-radius: 0.25rem;
    box-shadow: 3px 3px 3px -2px rgb(0 0 0 / 20%);
    cursor: pointer;

    &:hover {
      background-color: rgba($brand__color--blue, .15);
    }
  }

  &__complex {
    margin-bottom: 5px;
    margin-top: 5px;

    &__title {
      display: flex;
      align-items: center;

      &__icon-collapsed {
        width: 25px;
        height: 25px;
        border-radius: 100%;
        border: 1px solid $brand__color--blue;
        color: $brand__color--blue;
        background-color: rgba($brand__color--blue, 0.2);
        box-shadow: 3px 3px 3px -2px rgb(0 0 0 / 50%);
        margin-right: 5px;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: all linear 0.25s;

        &:hover {
          background-color: rgba($brand__color--blue, 0.3);
          box-shadow: 1px 1px 1px -2px rgb(0 0 0 / 20%);
        }
      }
    }

    &__group-children {
      margin-left: 15px;
    }
  }

  &__simple {
    margin-left: 30px;
    margin-bottom: 5px;
    margin-top: 5px;
    display: flex;
    align-items: center;
  }

  &__checkbox {
    margin-right: 5px;
    height: 16px;
    width: 16px;

    &:deep(.p-checkbox-box) {
      height: 100%;
      font-size: 12px;

      & > .p-checkbox-icon  {
        font-size: inherit;
      }
    }
  }

  &__title {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__legend__content__img{
    margin-right: 5px;
  }


}
</style>
